import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { theme } from 'styled-tools';

import Box from '@ui/box/Box.tsx';

import { useCSSColors } from '../../../../../src/hooks/useCSSColors.ts';
import BuildingAccordion from './BuildingAccordion.tsx';

const BuildingsWrapper = styled(Box)`
  filter: drop-shadow(${theme('shadows.md')});
  border-radius: ${theme('radii.lg')};
`;

const ArrowBox = styled(Box)`
  inset-inline-end: 8px;
`;

const Arrow = props => (
  <ArrowBox aria-hidden="true" position="absolute" display="flex" top="-9px" height="9px" width="19px">
    <svg aria-hidden="true" width="19" height="9" viewBox="0 0 19 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        opacity={props.opacity}
        d="M7.99485 0.720175C8.79167 -0.19048 10.2083 -0.190479 11.0052 0.720175L18.25 9H0.75L7.99485 0.720175Z"
        fill={props.iconFill}
      />
    </svg>
  </ArrowBox>
);

// NOTE: Implements ARIA TreeView semantics (roving tabindex, hierarchical navigation)
// Current limitation: collapsing the currently selected building is not supported by upstream state model
// (only one building can be expanded – the selected one – and there is no 'unselect building' event).
// Keyboard behavior implemented: Up/Down, Home/End navigation over visible items; Right expands or enters first child;
// Left moves focus to parent (from level to building). Enter/Space activate (select) items.
const BuildingsList = ({ ref, ...props }) => {
  const {
    buildings,
    selectedBuildingId,
    selectedLevelId,
    onBuildingClick,
    onLevelClick,
    T,
    levelSelectorRef,
    ...omittedProps
  } = props;
  const colors = useCSSColors();
  const treeContainerRef = useRef(null);

  // Roving tabindex focus id (building-* or level-*)
  const [focusedItemId, setFocusedItemId] = useState(null);

  // Flatten the currently visible tree structure into linear list for keyboard navigation.
  const visibleItems = useMemo(() => {
    if (!Array.isArray(buildings)) {
      return [];
    }
    const items = [];
    buildings.forEach(b => {
      items.push({
        type: 'building',
        id: b.id,
        itemId: `building-${b.id}`,
        hasChildren: Array.isArray(b.levels) && b.levels.length > 0,
      });
      if (selectedBuildingId === b.id && Array.isArray(b.levels)) {
        b.levels.forEach(lvl =>
          items.push({
            type: 'level',
            id: lvl.id,
            buildingId: b.id,
            itemId: `level-${lvl.id}`,
          }),
        );
      }
    });
    return items;
  }, [buildings, selectedBuildingId]);

  // Initialize focus on selected level, else selected building, else first building.
  useEffect(() => {
    if (!visibleItems.length) {
      return;
    }
    if (selectedLevelId) {
      const levelItem = visibleItems.find(i => i.type === 'level' && i.id === selectedLevelId);
      if (levelItem) {
        return setFocusedItemId(levelItem.itemId);
      }
    }
    if (selectedBuildingId) {
      const buildingItem = visibleItems.find(i => i.type === 'building' && i.id === selectedBuildingId);
      if (buildingItem) {
        return setFocusedItemId(buildingItem.itemId);
      }
    }
    if (!focusedItemId) {
      setFocusedItemId(visibleItems[0].itemId);
    }
  }, [visibleItems, selectedLevelId, selectedBuildingId]);

  const focusByIndex = useCallback(
    newIndex => {
      if (newIndex < 0 || newIndex >= visibleItems.length) {
        return;
      }
      setFocusedItemId(visibleItems[newIndex].itemId);
    },
    [visibleItems],
  );

  const handleKeyDown = useCallback(
    (e, currentItem) => {
      console.log('!e:', e);
      if (!currentItem) {
        return;
      }
      const index = visibleItems.findIndex(i => i.itemId === currentItem.itemId);
      if (index === -1) {
        return;
      }
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          focusByIndex(index + 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          focusByIndex(index - 1);
          break;
        case 'Home':
          e.preventDefault();
          focusByIndex(0);
          break;
        case 'End':
          e.preventDefault();
          focusByIndex(visibleItems.length - 1);
          break;
        case 'ArrowRight':
          if (currentItem.type === 'building') {
            if (currentItem.hasChildren && selectedBuildingId !== currentItem.id) {
              e.preventDefault();
              onBuildingClick(currentItem.id);
            } else if (currentItem.hasChildren && selectedBuildingId === currentItem.id) {
              // Move to first child if expanded
              const next = visibleItems[index + 1];
              if (next && next.type === 'level') {
                e.preventDefault();
                focusByIndex(index + 1);
              }
            }
          }
          break;
        case 'ArrowLeft':
          if (currentItem.type === 'level') {
            // Move to parent building
            const parentIdx = visibleItems
              .slice(0, index)
              .reverse()
              .find(i => i.type === 'building');
            if (parentIdx) {
              e.preventDefault();
              setFocusedItemId(parentIdx.itemId);
            }
          }
          break;
        case 'Enter':
        case ' ': {
          if (currentItem.type === 'building') {
            e.preventDefault();
            onBuildingClick(currentItem.id);
          } else if (currentItem.type === 'level') {
            e.preventDefault();
            onLevelClick(currentItem.id);
          }
          break;
        }
        default:
          break;
      }
    },
    [visibleItems, focusByIndex, onBuildingClick, onLevelClick, selectedBuildingId],
  );

  // Helper to get per-item props
  const getTreeItemProps = useCallback(
    item => ({
      treeTabIndex: focusedItemId === item.itemId ? 0 : -1,
      treeOnKeyDown: e => handleKeyDown(e, item),
      isTreeFocused: focusedItemId === item.itemId,
    }),
    [focusedItemId, handleKeyDown],
  );

  // After focusedItemId changes, move actual DOM focus so user sees focus ring and screen readers announce item.
  useEffect(() => {
    if (!treeContainerRef.current) {
      return;
    }
    const el = treeContainerRef.current.querySelector('[data-tree-focused="true"]');
    if (el && el !== document.activeElement && levelSelectorRef.current.contains(document.activeElement)) {
      // Avoid scrolling the container unexpectedly
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    }
  }, [focusedItemId]);

  return (
    <Box
      data-testid="BuildingsListView"
      ref={ref}
      position="relative"
      height="auto"
      maxHeight="100%"
      minHeight="0"
      display="flex"
      flexDirection="column"
      {...omittedProps}
    >
      {Array.isArray(buildings) && buildings.length > 0 && (
        <Arrow iconFill={colors.mapNavBackground} opacity={selectedBuildingId === buildings[0].id ? '1' : '0.85'} />
      )}

      <BuildingsWrapper display="flex" flexDirection="column" overflow="hidden">
        <Box
          data-testid="ScrollableBuildingsList"
          overflow="auto"
          height="auto"
          maxHeight="100%"
          role="tree"
          aria-label="Building list dropdown"
          id="Building dropdown"
          ref={treeContainerRef}
        >
          {buildings.map(building => {
            const buildingItem = visibleItems.find(i => i.type === 'building' && i.id === building.id);
            return (
              <BuildingAccordion
                data-testid="BuildingAccordion"
                key={building.id}
                selectedBuildingId={selectedBuildingId}
                selectedLevelId={selectedLevelId}
                onBuildingClick={onBuildingClick}
                onLevelClick={onLevelClick}
                {...(buildingItem ? getTreeItemProps(buildingItem) : { treeTabIndex: -1 })}
                focusedItemId={focusedItemId}
                getTreeItemProps={getTreeItemProps}
                selectedBuildingHasFocus={buildingItem && buildingItem.itemId === focusedItemId}
                {...building}
                T={T}
              />
            );
          })}
        </Box>
      </BuildingsWrapper>
    </Box>
  );
};

BuildingsList.displayName = 'Building Accordion';

export default BuildingsList;
